import { isDefined } from "@clipboard-health/util-ts";
import { type OnboardingStepWrapperProps } from "@src/appV2/redesign/Onboarding/OnboardingFlowContainer/OnboardingStepWrapper";
import { useSaveOnboardingStageEffect } from "@src/appV2/redesign/Onboarding/OnboardingFlowContainer/useSaveOnboardingStageEffect";
import { useGetWorker } from "@src/appV2/Worker/api/useGetWorker";

import { OnboardingPersonalIdError } from "./Error";
import { OnboardingPersonalIdVerificationLoading } from "./Loading";
import { OnboardingPersonalIdNotRequired } from "./NotRequired";
import { OnboardingPersonalIdVerificationPage } from "./Page";
import { type OnboardingPersonalIdVerificationAnalytics } from "./types";
import { useGetPersonalIdRequirement } from "./useGetPersonalIdRequirement";

type OnboardingPersonalIdVerificationContainerProps = Pick<
  OnboardingStepWrapperProps,
  "worker" | "onBack" | "onNext" | "progress" | "stage"
>;

export function OnboardingPersonalIdVerificationContainer({
  worker,
  onBack,
  onNext,
  progress,
  stage,
}: OnboardingPersonalIdVerificationContainerProps) {
  const {
    isLoading,
    personalIdRequirement,
    isPersonalIdRequired,
    isFetching,
    refetch,
    qualificationsError,
    workerRequirementsError,
  } = useGetPersonalIdRequirement();
  // Persist the onboarding stage to the backend
  useSaveOnboardingStageEffect({ worker, stage });

  const { refetch: refetchWorker } = useGetWorker({
    enabled: false,
  });

  const handleNext = async () => {
    await refetchWorker();
    await onNext();
  };

  const analytics = {
    workerId: worker?.userId,
  } satisfies OnboardingPersonalIdVerificationAnalytics;

  // Wait for loading to make sure that we render verification page knowing the exact status to avoid UI flickering
  if (isLoading) {
    return <OnboardingPersonalIdVerificationLoading progress={progress} />;
  }

  if (isDefined(personalIdRequirement)) {
    const automatedNameUpdateDocument = personalIdRequirement.documents?.find(
      (document) => document.automatedNameUpdate
    );
    const automatedNameUpdate = automatedNameUpdateDocument?.automatedNameUpdate;

    return (
      <OnboardingPersonalIdVerificationPage
        requirementId={personalIdRequirement.requirementId}
        requirementStatus={personalIdRequirement.status}
        analytics={analytics}
        progress={progress}
        automatedNameUpdate={automatedNameUpdate}
        onBack={onBack}
        onNext={handleNext}
      />
    );
  }

  // This should never happen since we show Personal ID Verification after qualification selection
  // In production we have Personal ID requirement mapped for all qualifications
  if (isPersonalIdRequired === false) {
    return (
      <OnboardingPersonalIdNotRequired
        analytics={analytics}
        progress={progress}
        onBack={onBack}
        onNext={async () => {
          await onNext();
        }}
      />
    );
  }

  return (
    <OnboardingPersonalIdError
      isLoading={isFetching}
      analytics={analytics}
      qualificationsError={qualificationsError}
      workerRequirementsError={workerRequirementsError}
      progress={progress}
      onTryAgain={refetch}
      onBack={onBack}
    />
  );
}
